import { useMemo, useState } from 'react';
import {
  components,
  ControlProps,
  ActionMeta,
  OnChangeValue,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { searchMultiSelectStyles } from './SearchMultiSelectStyleConfig';
import { OptionType } from '../MultiSelect/multiSelectStyleConfig';

interface SearchMultiSelectProps {
  defaultValue?: OptionType[];
  options: OptionType[];
  placeholder: string;
  onChange: (
    newValue: OnChangeValue<OptionType, true>,
    action: ActionMeta<OptionType>,
  ) => void;
  onInputChange: (input: string) => void;
  noOptionsMessage?: () => string;
  leftIcon?: React.FC | React.ComponentType;
  loading?: boolean;
  minSearchLength?: number;
}

const CustomControl = (IconComponent: React.ComponentType) => {
  function Control(props: ControlProps<OptionType, true>) {
    return (
      <components.Control {...props}>
        <div
          style={{
            marginLeft: 8,
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <IconComponent />
        </div>
        {props.children}
      </components.Control>
    );
  }

  Control.displayName = 'CustomControl';
  return Control;
};

function SearchMultiSelect({
  defaultValue,
  options,
  placeholder,
  onChange,
  onInputChange,
  noOptionsMessage,
  leftIcon,
  loading = false,
  minSearchLength = 0,
}: Readonly<SearchMultiSelectProps>) {
  const [hasSearched, setHasSearched] = useState(false);

  const handleInputChange = (input: string) => {
    const userIsTyping = input.length >= minSearchLength;
    setHasSearched(userIsTyping);
    onInputChange(input);
  };

  const shouldShowMenu =
    hasSearched && !loading && (options.length > 0 || !options.length);
  const componentsOverride = useMemo(
    () => ({
      Control: leftIcon ? CustomControl(leftIcon) : undefined,
      DropdownIndicator: () => null,
    }),
    [leftIcon],
  );
  return (
    <CreatableSelect
      isMulti
      value={defaultValue}
      options={options}
      styles={searchMultiSelectStyles}
      placeholder={placeholder}
      onChange={onChange}
      onInputChange={handleInputChange}
      noOptionsMessage={noOptionsMessage}
      isValidNewOption={() => false}
      formatCreateLabel={() => null}
      openMenuOnClick={false}
      openMenuOnFocus={false}
      menuIsOpen={shouldShowMenu}
      isLoading={loading}
      components={componentsOverride}
    />
  );
}
SearchMultiSelect.displayName = 'SearchMultiSelect';

export default SearchMultiSelect;
